import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import { TailwindButton, TailwindModal } from '../../components/Tailwind';
import { saveAs } from 'file-saver';
import {
    getReport,
    submitWorksheetJob,
    pollWorksheetJob,
    downloadWorksheetJobFile,
    downloadExcelValuesOnly,
    downloadExcelSensitivityValuesOnly,
} from '../../shared/api/tools';
export default function Tools() {
    const { model, currentScenario, currentSensitivity, currentEntity } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }

    return <ToolsContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} />;
}

// Content component: business logic and rendering
function ToolsContent({ currentEntity, model, currentScenario, currentSensitivity }) {
    const [loading, setLoading] = useState<string | null>(null);
    const [notComputedError, setNotComputedError] = useState(false);
    const [progress, setProgress] = useState<{ current: number; total: number; label: string } | null>(null);
    const [validationModal, setValidationModal] = useState<{ isOpen: boolean; jobId: string | null }>({ isOpen: false, jobId: null });

    useEffect(() => {
        if (!notComputedError) return undefined;
        const timer = setTimeout(() => setNotComputedError(false), 6000);
        return () => clearTimeout(timer);
    }, [notComputedError]);

    const reports = [
        { name: 'Assumptions Booklet', routeName: 'assumptions_booklet', description: 'Download the assumptions booklet for this model' },
        { name: 'Main Financial Model Terms', routeName: 'main_model_terms', description: 'Download the main financial model terms' },
        { name: 'All Financial Model Terms', routeName: 'all_model_terms', description: 'Download all financial model terms' },
    ];

    const handleDownload = async (fileName: string, routeName: string) => {
        setLoading(routeName);
        try {
            const response = await getReport(currentScenario.id, routeName);
            if (response && response.status === 409) {
                setNotComputedError(true);
                return;
            }
            if (response && response.ok) {
                const blob = await response.blob();
                const fullFileName = `${model.name} - ${currentScenario.name} - ${fileName}`;
                saveAs(blob, fullFileName);
            }
        } catch (error) {
            console.error('Error downloading report:', error);
        } finally {
            setLoading(null);
        }
    };

    const handleExcelDownload = async () => {
        setLoading('excel_export');
        setProgress({ current: 0, total: 10, label: "Starting..." });

        try {
            // 1. Submit job
            const submitResponse = await submitWorksheetJob(
                currentScenario.id,
                currentSensitivity?.id
            );

            if (!submitResponse || submitResponse.status === 409) {
                setNotComputedError(true);
                return;
            }

            if (submitResponse.status !== 202) {
                console.error('Failed to submit worksheet job');
                return;
            }

            const { job_id } = await submitResponse.json();

            // 2. Poll with progress
            const result = await pollWorksheetJob(
                job_id,
                (current, total, label) => {
                    setProgress({ current, total, label });
                }
            );

            if (!result) {
                console.error("Worksheet job returned null");
                return;
            }

            // 3. Check validation result
            if (result.success) {
                // Auto-download
                const response = await downloadWorksheetJobFile(job_id);
                if (response) {
                    const blob = await response.blob();
                    const entityName = currentSensitivity ? currentSensitivity.name : currentScenario.name;
                    saveAs(blob, `${model.name} - ${entityName}.xlsx`);
                }
            } else {
                // Show modal with options
                setValidationModal({ isOpen: true, jobId: job_id });
            }
        } catch (error) {
            console.error('Error during Excel export:', error);
        } finally {
            setLoading(null);
            setProgress(null);
        }
    };

    const handleDownloadAnyway = async () => {
        if (!validationModal.jobId) return;
        try {
            const response = await downloadWorksheetJobFile(validationModal.jobId);
            if (response) {
                const blob = await response.blob();
                const entityName = currentSensitivity ? currentSensitivity.name : currentScenario.name;
                saveAs(blob, `${model.name} - ${entityName}.xlsx`);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
        setValidationModal({ isOpen: false, jobId: null });
    };

    const handleDownloadValuesOnly = async () => {
        setValidationModal({ isOpen: false, jobId: null });
        setLoading('excel_export');
        try {
            let response;
            if (currentSensitivity) {
                response = await downloadExcelSensitivityValuesOnly(currentScenario.id, currentSensitivity.id);
            } else {
                response = await downloadExcelValuesOnly(currentScenario.id);
            }
            if (response) {
                const blob = await response.blob();
                const entityName = currentSensitivity ? currentSensitivity.name : currentScenario.name;
                saveAs(blob, `${model.name} - ${entityName} (Values Only).xlsx`);
            }
        } catch (error) {
            console.error('Error downloading values-only file:', error);
        } finally {
            setLoading(null);
        }
    };

    const handleCancelModal = () => {
        setValidationModal({ isOpen: false, jobId: null });
    };

    const progressPercent = progress ? Math.round((progress.current / progress.total) * 100) : 0;

    return (
        <div style={{ height: "100%", overflow: "auto" }}>
            <div className="flex flex-col h-full">
                <FMEPageHeader
                    title="Tools & Reports"
                    breadcrumb={[model.name]}
                    description="Download reports and files related to your financial model."
                    actions={<ScenarioSensitivitySelector />}
                />

                <motion.div
                    className="px-8 py-6"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.16 } }}
                >
                <div className="flex flex-col gap-6">
                    {/* Reports Section */}
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-semibold text-slate-900">Reports</h2>
                            <p className="text-slate-600">
                                Download various reports for your financial model. These reports provide detailed information about your model's assumptions and terms.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            {reports.map((report, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-5 border-2 border-slate-200 rounded-lg hover:border-primary/30 hover:bg-slate-50 transition-all"
                                >
                                    <div className="flex flex-col gap-1">
                                        <span className="text-slate-900 font-semibold text-base">
                                            {report.name}
                                        </span>
                                        <span className="text-sm text-slate-600">
                                            {report.description}
                                        </span>
                                    </div>

                                    <TailwindButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleDownload(report.name, report.routeName)}
                                        disabled={loading !== null}
                                        icon={loading === report.routeName ? undefined : "download"}
                                    >
                                        {loading === report.routeName ? (
                                            <div className="flex items-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                                                Downloading...
                                            </div>
                                        ) : (
                                            'Download'
                                        )}
                                    </TailwindButton>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Export to Excel Section */}
                    <div className="flex flex-col gap-4 mt-4">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-semibold text-slate-900">Export to Excel</h2>
                            <p className="text-slate-600">
                                Download your financial model as an Excel file with all formulas and data.
                            </p>
                        </div>

                        <div className="flex items-center justify-between p-5 border-2 border-emerald-200 bg-emerald-50/50 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all">
                            <div className="flex items-center gap-4">
                                <div className="flex flex-col gap-1">
                                    <span className="text-slate-900 font-semibold text-base">
                                        {currentSensitivity ? `${currentSensitivity.name} (Sensitivity)` : currentScenario.name}
                                    </span>
                                    <span className="text-sm text-slate-600">
                                        Excel file based on your selected scenario, including input and output data
                                    </span>
                                </div>
                            </div>

                            {loading === 'excel_export' && progress ? (
                                <div className="flex flex-col gap-1.5 min-w-[220px]">
                                    <div className="flex items-center justify-between text-xs text-slate-600">
                                        <span className="truncate max-w-[160px]">{progress.label}</span>
                                        <span className="font-medium">{progressPercent}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
                                            style={{ width: `${progressPercent}%` }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <TailwindButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleExcelDownload}
                                    disabled={loading !== null}
                                    icon={loading === 'excel_export' ? undefined : "download"}
                                >
                                    {loading === 'excel_export' ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                                            Downloading...
                                        </div>
                                    ) : (
                                        'Download'
                                    )}
                                </TailwindButton>
                            )}
                        </div>
                    </div>

                    {/* Custom Documents Section */}
                    <div className="flex flex-col gap-4 mt-4">
                        <h2 className="text-xl font-semibold text-slate-900">Custom Documents</h2>
                    <div className="flex items-center gap-4 p-5 border-2 border-slate-200 rounded-lg bg-slate-50/50">
                        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg shrink-0">
                            <span className="material-symbols-outlined text-primary text-[22px]">contact_support</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <p className="text-sm text-slate-600">
                                All documents can be customized upon request. Please contact the Peerception team to discuss your specific needs.
                            </p>
                        </div>
                    </div>
                    </div>
                </div>
            </motion.div>

            {notComputedError && (
                <div className="fixed bottom-6 inset-x-0 mx-auto w-fit z-50 flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg shadow-lg">
                    <span className="material-symbols-outlined text-amber-500 text-[20px]">warning</span>
                    <span className="text-sm font-medium">The model cannot be exported. Please fix any errors in your inputs and recompute the model.</span>
                    <button onClick={() => setNotComputedError(false)} className="ml-2 text-amber-500 hover:text-amber-700 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            <TailwindModal
                isOpen={validationModal.isOpen}
                onClose={handleCancelModal}
                title="Validation Incomplete"
                size="md"
                footer={
                    <div className="flex gap-3 justify-end w-full">
                        <TailwindButton variant="primary" onClick={handleDownloadValuesOnly}>
                            Download values only
                        </TailwindButton>
                        <TailwindButton variant="secondary" onClick={handleDownloadAnyway}>
                            Download with formulas
                        </TailwindButton>
                        <TailwindButton variant="ghost" onClick={handleCancelModal}>
                            Cancel
                        </TailwindButton>
                    </div>
                }
            >
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-amber-600 text-[20px]">warning</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-slate-700">
                            The generated excel file has differences with the values computed in Peerception Model.
                            The team has been notified and the issue will be resolved.
                        </p>
                        <p className="text-xs text-slate-500">
                            You can download the file with formulas anyway, or download a version with computed values only.
                        </p>
                    </div>
                </div>
            </TailwindModal>

            </div>
        </div>
    );
}
